// AssignNumberDialog.tsx — Nummer-Zuordnen-Dialog (Telefonie SP4, A9).
//
// Wird VOR einer Mail-/Vorgang-Aktion auf einem Anruf OHNE `contactId` geöffnet:
// die (unbekannte) Nummer des Anrufs muss zuerst einem Kontakt zugeordnet werden.
// Zwei Wege (analog zum Mail-Zuweisungsflow):
//   (1) Bestehenden Kontakt SUCHEN  → die Nummer wird als weiterer
//       Kommunikationskanal an den Kontakt angehängt (PUT /contact/:id, ohne
//       Bestandskanäle zu verlieren) und der Anruf zugeordnet
//       (PATCH /telephony/calls/:id { contactId }).
//   (1a) Bei einer Person wird vorher gefragt, ob es ihre PRIVATE oder ihre
//        GESCHÄFTLICHE Nummer ist (PO 27.07.2026) — die Antwort wird zum `label`
//        des Kanals, damit die Nummer in der Kontaktmaske in der richtigen Zeile
//        steht. Bei einer Firma entfällt die Frage (dort gibt es kein „privat").
//   (2) Kontakt ANLEGEN: Art wählen (Privat/Firma, Festnetz/Mobil, privat/
//       geschäftlich), dann öffnet sich die VOLLSTÄNDIGE Kontaktanlage als
//       Fenster (`openContactFormWindow`) mit der Nummer im passenden Feld.
//       Nach dem Speichern wird der Anruf zugeordnet und die Nummer im Verlauf
//       nachgezogen. Der Dialog selbst schließt sich beim Öffnen der Anlage.
//       Nur wo es keinen WindowManager gibt, bleibt die alte Mini-Anlage
//       (Name + Nummer, optional als Ansprechpartner einer Firma) als Ausweg.
// Nach erfolgreicher Zuordnung meldet der Dialog den aktualisierten Anruf via
// `onAssigned`; die auslösende Aktion (Mail/Vorgang) läuft dann im Aufrufer weiter.
// Abbruch (X/ESC/Backdrop/„Abbrechen") lässt den Anruf unverändert.
//
// Dritter Einstieg (`purpose: "nachAnruf"`): direkt nach einem Gespräch mit einer
// Nummer, die KMVS nicht kennt. Dort gibt es noch keinen CallLog-Eintrag — die
// Anlage liefert ihn erst später — also kommt statt `call` nur die `number`.
// Um den gerade beendeten Anruf muss sich hier niemand kümmern: Beim Anlegen
// sucht `upsertFromCdr` den Kontakt selbst, und die Nummer hängt dann schon dran.
// Zusätzlich läuft in JEDEM Fall `telephony/numbers/link` — das holt die
// bisherigen Anrufe derselben Nummer nach, die bis heute namenlos im Verlauf
// stehen, weil ihr Kontakt bei ihrer Anlage noch nicht zu finden war.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { apiUrl } from "../../utils/api";
import { useAuth } from "../../context/AuthContext";
import { useWindowManagerOptional } from "../windowManager/WindowManagerContext";
import { openContactFormWindow } from "../windowManager/ContactFormWindow";
import { zeigeGlobalenHinweis } from "../calendar/globalerHinweis";
import { telephonyService, type CallLogDto } from "../../services/telephonyService";
import { attachMenschen } from "../../services/menschenFuersUnternehmenService";
import { buildMenschenItems } from "../verantwortlichkeit/buildMenschenItems";
import type { ContactRef } from "../relationsInline/types";
import { counterpartyNumber } from "./telephonyUtils";
import { contactTypeLabel } from "../../utils/contactTypeLabel";
import {
    buildAppendedCommunications,
    effektiveFirma,
    firmenDesKontakts,
    guessNumberType,
    istFirmenkontakt,
    kontaktanlageVorbelegung,
    labelFuerZeile,
    vorbelegteFirma,
    zuordnungFehler,
    COMM_TYPE_LABEL,
    type CommKind,
    type CommType,
    type ExistingComm,
    type FirmaOption,
} from "./assignNumberUtils";
import "../redesign/contactRedesign.css";
import { alleFenster } from "../windowManager/browserFenster/fensterRegister";

const SearchIcon = (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round">
        <circle cx="9" cy="9" r="6" />
        <path d="M14 14l3.5 3.5" />
    </svg>
);
const CloseIcon = (
    <svg width="17" height="17" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round">
        <path d="M5 5l10 10M15 5L5 15" />
    </svg>
);

function nameOf(c: any): string {
    return (
        c?.displayName ||
        `${c?.lastname ?? ""}${c?.firstName ? ", " + c.firstName : ""}`.replace(/^,\s*|,\s*$/g, "").trim() ||
        "—"
    );
}

// ContactType-IDs (Person/Firma) einmal auflösen + modulweit cachen (wie
// RelationContactPicker). Ohne Treffer bleiben die Ids undefined → das Backend
// nimmt seinen Default-Typ.
let typeCache: { person?: number; firma?: number } | null = null;
async function resolveTypeIds(): Promise<{ person?: number; firma?: number }> {
    if (typeCache) return typeCache;
    try {
        const res = await fetch(apiUrl("contacttype"));
        const list = await res.json();
        const find = (n: string) =>
            Array.isArray(list) ? list.find((t: any) => String(t?.type).toLowerCase() === n)?.id : undefined;
        typeCache = { person: find("person"), firma: find("firma") };
    } catch {
        typeCache = {};
    }
    return typeCache;
}

// ── kleine, wiederverwendbare Kontakt-Suchbox (GET /contact?search, debounced) ──
// Exportiert: „Kontakt hinzufügen" im Anruf-Detail sucht mit derselben Box.
export const ContactSearchBox: React.FC<{
    placeholder: string;
    onlyType?: "Person" | "Firma";
    onPick: (contact: any) => void;
    autoFocus?: boolean;
    disabled?: boolean;
}> = ({ placeholder, onlyType, onPick, autoFocus, disabled }) => {
    const [q, setQ] = useState("");
    const [results, setResults] = useState<any[]>([]);
    const [searching, setSearching] = useState(false);

    useEffect(() => {
        const term = q.trim();
        if (term.length < 2) {
            setResults([]);
            return;
        }
        const ctrl = new AbortController();
        const t = setTimeout(() => {
            setSearching(true);
            fetch(apiUrl("contact", { search: term, limit: 50 }), { signal: ctrl.signal })
                .then((r) => r.json())
                .then((d) => {
                    const arr = Array.isArray(d) ? d : [];
                    const filtered = onlyType
                        ? arr.filter((c: any) => String(c?.type?.type ?? "").toLowerCase() === onlyType.toLowerCase())
                        : arr;
                    setResults(filtered.slice(0, 12));
                })
                .catch(() => {
                    /* abort/err ignorieren */
                })
                .finally(() => setSearching(false));
        }, 300);
        return () => {
            clearTimeout(t);
            ctrl.abort();
        };
    }, [q, onlyType]);

    return (
        <div className="rcp">
            <div className="rcp-search">
                {SearchIcon}
                <input
                    className="rcp-input"
                    placeholder={placeholder}
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    autoFocus={autoFocus}
                    disabled={disabled}
                />
            </div>
            {searching && <div className="rcp-msg">Suche …</div>}
            {!searching && q.trim().length >= 2 && results.length === 0 && (
                <div className="rcp-msg">Keine Treffer.</div>
            )}
            {results.length > 0 && (
                <div className="rcp-results">
                    {results.map((c) => (
                        <button
                            type="button"
                            key={c.id}
                            className="rcp-result"
                            onClick={() => onPick(c)}
                            disabled={disabled}
                        >
                            <span className="rcp-result-name">{nameOf(c)}</span>
                            <span className="rcp-result-typ">{contactTypeLabel(c.type?.type) || ""}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export interface AssignNumberDialogProps {
    /**
     * Der Anruf, von dem aus zugeordnet wird. Er fehlt bei `purpose: "nachAnruf"`:
     * Dort ist das Gespräch gerade erst zu Ende und die Telefonanlage hat den
     * Datensatz noch nicht geliefert. Dann trägt {@link number} die Rufnummer,
     * und der Verlauf wird serverseitig nachgezogen, sobald er eintrifft.
     */
    call?: CallLogDto | null;
    /** Die Rufnummer — nötig, wenn kein `call` vorliegt. */
    number?: string | null;
    /**
     * Nur für Kopf-/Hinweistext: welche Aktion nach der Zuordnung folgt.
     * `kontakt` = der „Kontakt"-Knopf im Verlauf — nur zuordnen, keine Aktion.
     */
    purpose: "mail" | "vorgang" | "nachAnruf" | "kontakt";
    /** Direkt im Anlegen-Modus öffnen (z. B. „Kontakt mit dieser Nummer anlegen"). */
    startModus?: "search" | "create";
    onClose: () => void;
    /**
     * Meldet den zugeordneten (aktualisierten) Anruf; der Aufrufer setzt seine
     * Aktion fort. Ohne `call` gibt es keinen zu melden — dann kommt `null`
     * samt der contactId, an die die Nummer gegangen ist.
     */
    onAssigned: (updated: CallLogDto | null, contactId: number) => void;
}

const AssignNumberDialog: React.FC<AssignNumberDialogProps> = ({
    call,
    number: numberProp,
    purpose,
    startModus,
    onClose,
    onAssigned,
}) => {
    const { user } = useAuth();
    // Ohne WindowManager (außerhalb der App-Shell) gibt es kein Fenster für die
    // volle Kontaktanlage — dann bleibt die Mini-Anlage.
    const wm = useWindowManagerOptional();
    const meId = (user?.id as string | undefined) ?? undefined;

    const number = useMemo(
        () => (call ? counterpartyNumber(call) ?? "" : (numberProp ?? "").trim()),
        [call, numberProp],
    );

    const [mode, setMode] = useState<"search" | "create">(startModus ?? "search");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    // Anlage-Felder.
    const [ctype, setCtype] = useState<"Person" | "Firma">("Person");
    const [vorname, setVorname] = useState("");
    const [nachname, setNachname] = useState("");
    const [firmenname, setFirmenname] = useState("");
    const [numberVal, setNumberVal] = useState(number);
    const [numberType, setNumberType] = useState<CommType>(() => guessNumberType(number));
    // Privat oder geschäftlich — nur bei Person eine Frage (siehe Kopf).
    const [zuordnung, setZuordnung] = useState<CommKind>("privat");
    // Optionale AP-/„Mensch fürs Unternehmen"-Anbindung (nur Person).
    const [apEnabled, setApEnabled] = useState(false);
    const [apFirma, setApFirma] = useState<{ contactId: number; displayName: string } | null>(null);

    // Im Suchmodus gewählter Kontakt, dessen Zuordnung noch offen ist — samt
    // dem, was der Dialog über ihn wissen muss: seine Bestandskanäle (für den
    // Dubletten-Schutz und damit das PUT sie nicht verwirft) und seine
    // verbundenen Firmen (für „geschäftlich bei …"). Beides kommt aus EINEM
    // Abruf, bevor gefragt wird — sonst stünde die Firmen-Auswahl erst da,
    // nachdem der Nutzer schon geantwortet hat.
    const [ziel, setZiel] = useState<{
        contact: any;
        comms: ExistingComm[];
        firmen: FirmaOption[];
        istFirma: boolean;
    } | null>(null);
    const [zielLaedt, setZielLaedt] = useState(false);
    // Antworten zum gewählten Kontakt.
    const [zielType, setZielType] = useState<CommType>(() => guessNumberType(number));
    const [zielKind, setZielKind] = useState<CommKind | "">("");
    const [zielFirma, setZielFirma] = useState<number | null>(null);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        alleFenster.addEventListener("keydown", onKey);
        return () => alleFenster.removeEventListener("keydown", onKey);
    }, [onClose]);

    // Aktualisierten Anruf mit sicherem Anzeigenamen zurückmelden (falls das PATCH-
    // Ergebnis den Namen noch nicht aufgelöst hat, den bekannten Namen einsetzen).
    const finish = useCallback(
        (updated: CallLogDto | null, contactId: number, displayName: string | null) => {
            onAssigned(
                updated
                    ? {
                          ...updated,
                          contactId,
                          contactDisplayName: updated.contactDisplayName ?? displayName ?? null,
                      }
                    : null,
                contactId,
            );
        },
        [onAssigned],
    );

    /**
     * Die Nummer beim Kontakt verbuchen.
     *
     * Zwei Schritte, und der zweite ist der eigentliche Gewinn: Der vorliegende
     * Anruf wird direkt zugeordnet (nur wenn es ihn schon gibt), und das
     * Backend zieht danach ALLE übrigen Anrufe dieser Nummer nach — auch die
     * aus der Vergangenheit, die KMVS damals nicht zuordnen konnte. Das
     * Nachziehen ist best effort: Scheitert es, hängt die Nummer trotzdem am
     * Kontakt, nur der alte Verlauf bleibt unverändert.
     */
    const zuordnen = useCallback(
        async (contactId: number): Promise<CallLogDto | null> => {
            const updated = call ? await telephonyService.updateCall(call.id, { contactId }) : null;
            if (number.trim()) await telephonyService.linkNumberToContact(number, contactId);
            return updated;
        },
        [call, number],
    );

    // (1) Bestehenden Kontakt wählen → Nummer anhängen + Anruf zuordnen.
    const assignExisting = useCallback(async () => {
        if (saving || !ziel) return;
        // Ohne Nummer wird keine Zeile geschrieben — dann ist auch nichts zu
        // fragen. Der Dialog ordnet dann nur den Anruf dem Kontakt zu.
        const fehler = number.trim()
            ? zuordnungFehler({
                  istFirma: ziel.istFirma,
                  kind: zielKind,
                  firmen: ziel.firmen,
                  companyId: zielFirma,
              })
            : null;
        if (fehler) {
            setError(fehler);
            return;
        }
        setSaving(true);
        setError("");
        try {
            const contactId = Number(ziel.contact.id);
            if (number.trim()) {
                const nextComms = buildAppendedCommunications(ziel.comms, number, {
                    type: zielType,
                    label: labelFuerZeile(ziel.istFirma, zielKind),
                    kind: ziel.istFirma ? null : (zielKind as CommKind),
                    companyId: effektiveFirma(ziel.istFirma, zielKind, zielFirma),
                });
                // `null` heißt: die Nummer steht schon am Kontakt. Dann gibt es
                // nichts zu schreiben, der Anruf wird trotzdem zugeordnet.
                if (nextComms) {
                    const put = await fetch(apiUrl(`contact/${contactId}`), {
                        method: "PUT",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ communications: nextComms }),
                    });
                    if (!put.ok) throw new Error(`status ${put.status}`);
                }
            }
            const updated = await zuordnen(contactId);
            finish(updated, contactId, nameOf(ziel.contact));
        } catch {
            setError("Die Nummer konnte dem Kontakt nicht zugeordnet werden.");
            setSaving(false);
        }
    }, [saving, ziel, zielType, zielKind, zielFirma, number, zuordnen, finish]);

    /**
     * Treffer aus der Suche: Kontakt mit Kanälen UND Firmen-Verbindungen laden,
     * dann fragen. Die Firmen stehen nur in den Relationen, und zwar in beiden
     * Richtungen — `include=relations` liefert sie.
     *
     * Scheitert der Abruf, wird trotzdem gefragt: ohne Bestandskanäle greift
     * der Dubletten-Schutz nicht und ohne Firmen fehlt die Auswahl, aber die
     * Zuordnung als solche funktioniert. Ein harter Abbruch wäre hier die
     * schlechtere Antwort.
     */
    const pickExisting = useCallback(
        async (contact: any) => {
            const istFirma = istFirmenkontakt(contact?.type?.type);
            setError("");
            setZielLaedt(true);
            let full: any = null;
            try {
                const res = await fetch(
                    apiUrl(`contact/${contact.id}`, { include: "communications,relations" }),
                );
                full = res.ok ? await res.json() : null;
            } catch {
                /* siehe oben — ohne Details wird trotzdem gefragt */
            }
            const firmen = istFirma ? [] : firmenDesKontakts(full);
            setZiel({
                contact,
                comms: Array.isArray(full?.communications) ? full.communications : [],
                firmen,
                istFirma,
            });
            setZielType(guessNumberType(number));
            setZielKind("");
            setZielFirma(vorbelegteFirma(firmen));
            setZielLaedt(false);
        },
        [number],
    );

    /** Zurück zur Suche — alle Antworten zum alten Ziel verwerfen. */
    const zielVerwerfen = useCallback(() => {
        setError("");
        setZiel(null);
        setZielKind("");
        setZielFirma(null);
    }, []);

    // (2) Minimalen Kontakt anlegen (+ optional AP-Firma) → Anruf zuordnen.
    const createAndAssign = useCallback(async () => {
        if (saving) return;
        setError("");
        if (ctype === "Firma") {
            if (!firmenname.trim()) {
                setError("Firmenname ist erforderlich.");
                return;
            }
        } else if (!nachname.trim()) {
            setError("Nachname ist erforderlich.");
            return;
        }
        if (ctype === "Person" && apEnabled && !apFirma?.contactId) {
            setError("Bitte eine bestehende Firma für die Ansprechpartner-Anbindung wählen.");
            return;
        }

        setSaving(true);
        try {
            const ids = await resolveTypeIds();
            const istFirma = ctype === "Firma";
            const comms: Array<Record<string, unknown>> = [];
            if (numberVal.trim()) {
                comms.push({
                    type: numberType,
                    label: labelFuerZeile(istFirma, zuordnung),
                    value: numberVal.trim(),
                    isDefault: true,
                    kind: istFirma ? null : zuordnung,
                    // Wird die Person gleich als Ansprechpartner einer Firma
                    // angelegt, ist bei „geschäftlich" auch klar, welche Firma
                    // gemeint ist — es gibt ja nur die eine.
                    companyId: effektiveFirma(
                        istFirma,
                        zuordnung,
                        apEnabled ? (apFirma?.contactId ?? null) : null,
                    ),
                });
            }

            let body: any;
            if (ctype === "Firma") {
                body = {
                    lastname: firmenname.trim(),
                    displayName: firmenname.trim(),
                    ...(ids.firma != null ? { type: { id: ids.firma } } : {}),
                };
            } else {
                body = {
                    lastname: nachname.trim(),
                    displayName: vorname.trim() ? `${nachname.trim()}, ${vorname.trim()}` : nachname.trim(),
                    ...(vorname.trim() ? { firstName: vorname.trim() } : {}),
                    ...(ids.person != null ? { type: { id: ids.person } } : {}),
                };
            }
            if (comms.length) body.communications = comms;
            // Ohne foUserId legt das Backend nicht-gfgl-Kontakte als accessLevel=TENANT
            // an (für alle sichtbar). Ersteller als FO setzen → gehört dem Anlegenden
            // (bei AP-Anbindung erbt attach-menschen anschließend FO/BO der Firma).
            if (meId) body.foUserId = meId;

            const created = await fetch(apiUrl("contact"), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            }).then(async (r) => {
                if (!r.ok) throw new Error(`status ${r.status}`);
                return r.json();
            });
            const contactId = Number(created.id);

            // AP-/„Mensch fürs Unternehmen"-Anbindung an eine bestehende Firma
            // (Status + FO/BO-Vererbung serverseitig, wie in der Firmen-Anlage).
            if (ctype === "Person" && apEnabled && apFirma?.contactId) {
                const ref: ContactRef = { contactId, type: "Person" };
                const items = buildMenschenItems(
                    [
                        {
                            ref,
                            verant: {
                                isExtern: false,
                                roles: [{ kind: "mensch", verantwortlichkeit: "Ansprechpartner" }],
                                externalCompany: null,
                            },
                        },
                    ],
                    apFirma.contactId,
                );
                if (items.length) await attachMenschen(items, false);
            }

            const updated = await zuordnen(contactId);
            finish(updated, contactId, nameOf(created));
        } catch {
            setError("Der Kontakt konnte nicht angelegt und zugeordnet werden.");
            setSaving(false);
        }
    }, [
        saving,
        ctype,
        firmenname,
        nachname,
        vorname,
        apEnabled,
        apFirma,
        numberVal,
        numberType,
        zuordnung,
        meId,
        zuordnen,
        finish,
    ]);

    /**
     * (2) Volle Kontaktanlage öffnen. Die Nummer steht dort schon im Feld, das
     * zur gewählten Art passt; Name und alles Weitere trägt man dort ein.
     *
     * Der Dialog schließt sich sofort — das Anlage-Fenster ist die eigentliche
     * Arbeit, und ein Modal darüber stünde im Weg. Die Zuordnung danach läuft
     * deshalb unabhängig vom Dialog: `zuordnen` und `onAssigned` sind hier
     * gebunden, ein Fehler geht an den globalen Hinweis statt an den (dann
     * geschlossenen) Dialog.
     */
    const openFullForm = useCallback(() => {
        if (!wm) return;
        const vorbelegung = kontaktanlageVorbelegung({
            istFirma: ctype === "Firma",
            type: numberType,
            kind: zuordnung,
            number: numberVal,
        });
        openContactFormWindow(wm.openWindow, {
            ...vorbelegung,
            onCreated: (contactId) => {
                void (async () => {
                    try {
                        const updated = await zuordnen(contactId);
                        onAssigned(updated ? { ...updated, contactId } : null, contactId);
                    } catch {
                        zeigeGlobalenHinweis(
                            "Der Kontakt wurde angelegt, der Anruf konnte ihm aber nicht zugeordnet werden. Bitte über „Kontakt“ in der Anrufliste erneut zuordnen.",
                            { art: "fehler" },
                        );
                    }
                })();
            },
        });
        onClose();
    }, [wm, ctype, numberType, zuordnung, numberVal, zuordnen, onAssigned, onClose]);

    // Warum der Dialog offen ist. Nach einem Gespräch folgt keine Aktion — da
    // geht es darum, dass der Anruf im Verlauf nicht namenlos stehen bleibt.
    const hinweis =
        purpose === "nachAnruf"
            ? "Diese Nummer gehört zu keinem Kontakt in KMVS. Ordne sie jetzt zu — dann steht sie im Verlauf mit Namen da, auch bei den früheren Anrufen."
            : purpose === "kontakt"
              ? "Ordne die Nummer einem bestehenden Kontakt zu oder lege einen neuen an — dann steht sie im Verlauf mit Namen da, auch bei den früheren Anrufen dieser Nummer."
              : `Dieser Anruf ist noch keinem Kontakt zugeordnet. Ordne die Nummer einem Kontakt zu, um die ${purpose === "mail" ? "E-Mail" : "Vorgang"}-Aktion fortzusetzen.`;

    return createPortal(
        <div className="kvp-rd kvp-rd-scrim" onClick={saving ? undefined : onClose}>
            <div
                className="dm"
                role="dialog"
                aria-modal="true"
                aria-label="Nummer einem Kontakt zuordnen"
                style={{ width: "min(560px, 100%)" }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="dm-head">
                    <div className="dm-head-top">
                        <div className="dm-titles">
                            <div className="dm-kicker">
                                {purpose === "nachAnruf"
                                    ? "Gespräch beendet"
                                    : purpose === "kontakt" && !call
                                      ? "Nummer zuordnen"
                                      : "Anruf zuordnen"}
                            </div>
                            <h2>{number ? `Nummer ${number} zuordnen` : "Anruf einem Kontakt zuordnen"}</h2>
                        </div>
                        <button className="dm-x" type="button" onClick={onClose} aria-label="Schließen">
                            {CloseIcon}
                        </button>
                    </div>
                </div>

                <div className="dm-body" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                    <div
                        style={{
                            font: "500 13px/1.45 var(--ui)",
                            color: "var(--ink-2, #55606b)",
                            background: "var(--surface-2, #f4f6f8)",
                            border: "1px solid var(--line, #e3e7eb)",
                            borderRadius: 10,
                            padding: "9px 12px",
                        }}
                    >
                        {hinweis}
                    </div>

                    {mode === "search" && zielLaedt ? (
                        <div className="rcp-msg">Kontakt wird geladen …</div>
                    ) : mode === "search" && ziel ? (
                        <div className="rcp-create">
                            <div className="fld">
                                <label>Nummer geht an</label>
                                <div className="rcp-selected">
                                    <span className="rcp-selected-name">{nameOf(ziel.contact)}</span>
                                    <button
                                        type="button"
                                        className="rcp-change"
                                        onClick={zielVerwerfen}
                                        disabled={saving}
                                    >
                                        ändern
                                    </button>
                                </div>
                            </div>

                            {/* Ohne Nummer gibt es keine Zeile zu schreiben —
                                dann ordnet der Dialog nur den Anruf zu. */}
                            {!!number.trim() && (
                                <div className="fld">
                                    <label>Art</label>
                                    <select
                                        className="sel"
                                        value={zielType}
                                        onChange={(e) => setZielType(e.target.value as CommType)}
                                        disabled={saving}
                                    >
                                        <option value="phone">{COMM_TYPE_LABEL.phone}</option>
                                        <option value="mobile">{COMM_TYPE_LABEL.mobile}</option>
                                    </select>
                                </div>
                            )}

                            {/* Eine Firma kennt die Unterscheidung nicht — dort
                                bleibt es bei der Art. */}
                            {!!number.trim() && !ziel.istFirma && (
                                <div className="fld">
                                    <label>
                                        Nummer ist <span className="req">*</span>
                                    </label>
                                    <select
                                        className="sel"
                                        value={zielKind}
                                        onChange={(e) => {
                                            const k = e.target.value as CommKind | "";
                                            setZielKind(k);
                                            // Bei genau einer verbundenen Firma steht die
                                            // Antwort fest; sonst muss gewählt werden.
                                            setZielFirma(
                                                k === "geschaeftlich"
                                                    ? vorbelegteFirma(ziel.firmen)
                                                    : null,
                                            );
                                            setError("");
                                        }}
                                        disabled={saving}
                                    >
                                        <option value="">bitte wählen …</option>
                                        <option value="privat">privat</option>
                                        <option value="geschaeftlich">geschäftlich</option>
                                    </select>
                                </div>
                            )}

                            {/* Die Firmen-Frage stellt sich nur bei einer
                                geschäftlichen Nummer und nur, wenn es überhaupt
                                verbundene Firmen gibt. */}
                            {!!number.trim() &&
                                !ziel.istFirma &&
                                zielKind === "geschaeftlich" &&
                                ziel.firmen.length > 0 && (
                                    <div className="fld">
                                        <label>
                                            Firma
                                            {ziel.firmen.length >= 2 && <span className="req"> *</span>}
                                        </label>
                                        <select
                                            className="sel"
                                            value={zielFirma ?? ""}
                                            onChange={(e) => {
                                                setZielFirma(e.target.value ? Number(e.target.value) : null);
                                                setError("");
                                            }}
                                            disabled={saving}
                                        >
                                            <option value="">bitte wählen …</option>
                                            {ziel.firmen.map((f) => (
                                                <option key={f.id} value={f.id}>
                                                    {f.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                            {error && <div className="rcp-err">{error}</div>}
                            <div className="rcp-create-actions">
                                <button
                                    type="button"
                                    className="rd-pop-btn"
                                    onClick={zielVerwerfen}
                                    disabled={saving}
                                >
                                    Zurück zur Suche
                                </button>
                                <button
                                    type="button"
                                    className="rd-pop-btn primary"
                                    onClick={() => void assignExisting()}
                                    disabled={saving}
                                >
                                    {saving ? "Zuordnen …" : "Zuordnen"}
                                </button>
                            </div>
                        </div>
                    ) : mode === "search" ? (
                        <>
                            <ContactSearchBox
                                placeholder="Privatkontakt oder Firma suchen …"
                                onPick={pickExisting}
                                autoFocus
                                disabled={saving}
                            />
                            <button
                                type="button"
                                className="rcp-newlink"
                                onClick={() => {
                                    setError("");
                                    setNumberVal(number);
                                    setNumberType(guessNumberType(number));
                                    setMode("create");
                                }}
                                disabled={saving}
                            >
                                + Neuen Kontakt anlegen
                            </button>
                        </>
                    ) : (
                        <div className="rcp-create">
                            <div className="fld">
                                <label>Typ</label>
                                <select
                                    className="sel"
                                    value={ctype}
                                    onChange={(e) => setCtype(e.target.value as "Person" | "Firma")}
                                >
                                    <option value="Person">Privatkontakt</option>
                                    <option value="Firma">Firma</option>
                                </select>
                            </div>

                            {/* Name & Co. trägt man in der vollen Kontaktanlage ein —
                                hier nur, wenn es kein Fenster dafür gibt. */}
                            {wm ? null : ctype === "Person" ? (
                                <div className="rcp-row2">
                                    <div className="fld">
                                        <label>Vorname</label>
                                        <input className="inp" value={vorname} onChange={(e) => setVorname(e.target.value)} />
                                    </div>
                                    <div className="fld">
                                        <label>
                                            Nachname <span className="req">*</span>
                                        </label>
                                        <input className="inp" value={nachname} onChange={(e) => setNachname(e.target.value)} />
                                    </div>
                                </div>
                            ) : (
                                <div className="fld">
                                    <label>
                                        Firmenname <span className="req">*</span>
                                    </label>
                                    <input className="inp" value={firmenname} onChange={(e) => setFirmenname(e.target.value)} />
                                </div>
                            )}

                            <div className="rcp-row2">
                                <div className="fld">
                                    <label>Nummer</label>
                                    <input className="inp" value={numberVal} onChange={(e) => setNumberVal(e.target.value)} />
                                </div>
                                <div className="fld">
                                    <label>Art</label>
                                    <select
                                        className="sel"
                                        value={numberType}
                                        onChange={(e) => setNumberType(e.target.value as CommType)}
                                    >
                                        <option value="phone">Festnetz</option>
                                        <option value="mobile">Mobil</option>
                                    </select>
                                </div>
                            </div>

                            {ctype === "Person" && (
                                <div className="fld">
                                    <label>Nummer ist</label>
                                    <select
                                        className="sel"
                                        value={zuordnung}
                                        onChange={(e) => setZuordnung(e.target.value as CommKind)}
                                    >
                                        <option value="privat">privat</option>
                                        <option value="geschaeftlich">geschäftlich</option>
                                    </select>
                                </div>
                            )}

                            {!wm && ctype === "Person" && (
                                <div className="fld">
                                    <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                                        <input
                                            type="checkbox"
                                            checked={apEnabled}
                                            onChange={(e) => {
                                                setApEnabled(e.target.checked);
                                                if (!e.target.checked) setApFirma(null);
                                            }}
                                        />
                                        Ansprechpartner einer Firma
                                    </label>
                                    {apEnabled &&
                                        (apFirma ? (
                                            <div className="rcp-selected">
                                                <span className="rcp-selected-name">{apFirma.displayName}</span>
                                                <button type="button" className="rcp-change" onClick={() => setApFirma(null)}>
                                                    ändern
                                                </button>
                                            </div>
                                        ) : (
                                            <ContactSearchBox
                                                placeholder="Firma suchen …"
                                                onlyType="Firma"
                                                onPick={(c) => setApFirma({ contactId: Number(c.id), displayName: nameOf(c) })}
                                                disabled={saving}
                                            />
                                        ))}
                                </div>
                            )}

                            {wm && (
                                <div className="rcp-msg" style={{ textAlign: "left" }}>
                                    Es öffnet sich die vollständige Kontaktanlage. Die Nummer ist dort
                                    schon eingetragen; Name und alles Weitere ergänzen Sie dort. Nach dem
                                    Speichern wird der Anruf dem neuen Kontakt zugeordnet.
                                </div>
                            )}

                            {error && <div className="rcp-err">{error}</div>}
                            <div className="rcp-create-actions">
                                <button
                                    type="button"
                                    className="rd-pop-btn"
                                    onClick={() => {
                                        setError("");
                                        setMode("search");
                                    }}
                                    disabled={saving}
                                >
                                    Zurück zur Suche
                                </button>
                                {wm ? (
                                    <button
                                        type="button"
                                        className="rd-pop-btn primary"
                                        onClick={openFullForm}
                                        disabled={saving}
                                    >
                                        Kontaktanlage öffnen
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        className="rd-pop-btn primary"
                                        onClick={createAndAssign}
                                        disabled={saving}
                                    >
                                        {saving ? "Anlegen …" : "Anlegen & zuordnen"}
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {mode === "search" && !ziel && error && <div className="rcp-err">{error}</div>}
                </div>

                <div className="dm-foot">
                    <button type="button" className="rd-pop-btn" onClick={onClose} disabled={saving}>
                        {/* Nach einem Gespräch bricht man nichts ab — man verschiebt es. */}
                        {purpose === "nachAnruf" ? "Später" : "Abbrechen"}
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default AssignNumberDialog;
